import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, KeyRound, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) return setError("Konfirmasi password tidak sama.");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2] p-6">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md" data-testid="reset-password-page">
        <div className="flex items-center gap-2 mb-8">
          <div className="h-9 w-9 rounded-xl bg-[#1F1B16] flex items-center justify-center overflow-hidden p-1.5">
            <img src="/logo-mark.png" alt="BedsolvingOps" className="h-full w-full object-contain" />
          </div>
          <div className="font-display text-lg font-bold text-slate-900">BedsolvingOps</div>
        </div>

        {!token ? (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" data-testid="reset-invalid">
            Link reset tidak valid. Silakan minta link baru dari halaman login.
          </div>
        ) : done ? (
          <div className="space-y-4">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 flex items-center gap-2" data-testid="reset-success">
              <CheckCircle2 className="h-4 w-4" /> Password berhasil direset. Silakan login dengan password baru.
            </div>
            <Button className="w-full h-11" onClick={() => navigate("/login")} data-testid="reset-goto-login">Ke halaman login</Button>
          </div>
        ) : (
          <>
            <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
              <KeyRound className="h-7 w-7 text-[#D96635]" /> Reset password
            </h1>
            <p className="text-slate-500 mt-2 text-sm">Masukkan password baru Anda.</p>
            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-form">
              <div className="space-y-1.5">
                <Label htmlFor="new-password">Password baru</Label>
                <Input id="new-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Minimal 6 karakter" required minLength={6} data-testid="reset-password-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm-password">Konfirmasi password</Label>
                <Input id="confirm-password" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Ulangi password baru" required minLength={6} data-testid="reset-confirm-input" />
              </div>
              {error && (
                <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="reset-error">{error}</div>
              )}
              <Button type="submit" className="w-full h-11 text-base" disabled={busy} data-testid="reset-submit-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan password baru"}
              </Button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
